import { CheckIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

import { AnimatedHeight } from "~/vendor/upstream/components/AnimatedHeight";
import { cn } from "~/lib/utils";
import {
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  DialogDescription,
} from "~/components/ui/dialog";

/**
 * Compose a wizard from its header, panel, and footer; flow logic stays with
 * the caller. Graduated from upstream's `components/ui/wizard.tsx` (parity pin
 * `e3e7cc3f`) rather than vendored: the upstream file imports upstream's own
 * dialog primitive, and a second vendored copy of the dialog would fork it.
 * Forge owns the dialog, so the wizard builds on forge's primitive here and
 * follows forge's token rules (the canvas rung is spelled `neutral-25`).
 */

export function WizardPopup({
  children,
  ...props
}: Omit<ComponentProps<typeof DialogPopup>, "className" | "style">) {
  return (
    <DialogPopup {...props} className="max-w-xl overflow-x-hidden overflow-y-auto">
      <div className="flex min-h-0 flex-col">{children}</div>
    </DialogPopup>
  );
}

export function WizardHeader({
  title,
  description,
  identity,
  children,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Optional branding shown in place of the visible title. The title remains accessible. */
  readonly identity?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <DialogHeader>
      <DialogTitle className={identity ? "sr-only" : undefined}>{title}</DialogTitle>
      {identity}
      {description ? <DialogDescription>{description}</DialogDescription> : null}
      {children}
    </DialogHeader>
  );
}

export function WizardFooter({
  children,
  leading,
}: {
  readonly children: ReactNode;
  readonly leading?: ReactNode;
}) {
  return (
    <DialogFooter variant="bare" className={leading ? "sm:justify-between" : undefined}>
      {leading}
      {leading ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row">{children}</div>
      ) : (
        children
      )}
    </DialogFooter>
  );
}

const STEP_ITEM_BASE_CLASS =
  "flex w-full min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:justify-center max-sm:px-2";
const STEP_ITEM_INTERACTIVE_CLASS =
  "cursor-pointer hover:bg-card disabled:cursor-default disabled:hover:bg-transparent";
const STEP_ITEM_CURRENT_CLASS =
  "bg-card text-foreground shadow-xs ring-1 ring-border hover:bg-card dark:shadow-none";
const STEP_LABEL_BASE_CLASS = "min-w-0 truncate text-sm font-medium max-sm:hidden";

function stepItemClass(interactive: boolean, isCurrent: boolean): string {
  if (!interactive) {
    return STEP_ITEM_BASE_CLASS;
  }
  if (isCurrent) {
    return cn(STEP_ITEM_BASE_CLASS, STEP_ITEM_INTERACTIVE_CLASS, STEP_ITEM_CURRENT_CLASS);
  }
  return cn(STEP_ITEM_BASE_CLASS, STEP_ITEM_INTERACTIVE_CLASS);
}

function stepCircleClass(index: number, currentStep: number): string {
  if (index < currentStep) return "bg-primary text-primary-foreground ring-primary";
  if (index === currentStep) return "bg-primary/10 text-primary ring-primary/30";
  return "bg-card text-muted-foreground ring-border dark:bg-input/32";
}

function stepLabelClass(isCurrent: boolean): string {
  return cn(STEP_LABEL_BASE_CLASS, isCurrent ? "text-foreground" : "text-muted-foreground");
}

function stepAriaLabel(
  step: string,
  index: number,
  currentStep: number,
  summaries: readonly (string | null)[] | undefined,
): string {
  const summary = index < currentStep ? (summaries?.[index] ?? null) : null;
  return summary === null ? `${step}, step ${index + 1}` : `${step}, step ${index + 1}, ${summary}`;
}

function stepSummaryText(
  showSummaries: boolean,
  index: number,
  currentStep: number,
  summaries: readonly (string | null)[] | undefined,
): string | null {
  if (!showSummaries || index >= currentStep) {
    return null;
  }
  const summary = summaries?.[index];
  return summary === undefined || summary === null ? null : `: ${summary}`;
}

export function WizardSteps({
  steps,
  currentStep,
  summaries,
  showSummaries = false,
  onStepChange,
  isStepDisabled,
}: {
  readonly steps: readonly string[];
  readonly currentStep: number;
  readonly summaries?: readonly (string | null)[];
  readonly showSummaries?: boolean;
  readonly isStepDisabled?: (step: number) => boolean;
  readonly onStepChange?: (step: number) => void;
}) {
  const Step = onStepChange ? "button" : "div";
  return (
    <ol
      className="grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-background p-1 ring-1 ring-border dark:bg-muted"
      role="list"
      aria-label="Setup progress"
    >
      {steps.map((step, index) => (
        <li key={step} className="min-w-0">
          <Step
            {...(onStepChange
              ? { type: "button" as const, disabled: isStepDisabled?.(index) }
              : {})}
            className={stepItemClass(onStepChange !== undefined, index === currentStep)}
            aria-current={index === currentStep ? "step" : undefined}
            aria-label={stepAriaLabel(step, index, currentStep, summaries)}
            onClick={onStepChange ? () => onStepChange(index) : undefined}
          >
            <span
              className={cn(
                "grid size-5 shrink-0 place-items-center rounded-full text-sm font-medium ring-1",
                stepCircleClass(index, currentStep),
              )}
              aria-hidden
            >
              {index < currentStep ? <CheckIcon className="size-4 shrink-0" /> : index + 1}
            </span>
            <span className={stepLabelClass(index === currentStep)}>
              {step}
              {stepSummaryText(showSummaries, index, currentStep, summaries)}
            </span>
          </Step>
        </li>
      ))}
    </ol>
  );
}

export function WizardPanel({
  children,
  holdHeight = false,
}: {
  readonly children: ReactNode;
  readonly holdHeight?: boolean;
}) {
  return (
    <div
      data-slot="dialog-panel"
      className="min-w-0 space-y-4 bg-background/80 px-6 py-5 ring-1 ring-border dark:bg-input/16"
    >
      <AnimatedHeight holdHeight={holdHeight}>{children}</AnimatedHeight>
    </div>
  );
}
